import React, { Component } from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import ChatContactsComp from 'components/Chat/ChatContacts';
import UnreadChatNav from 'components/Chat/UnreadChatNav/UnreadChatNav';
import classes from 'components/Chat/ChatList.module.scss';
import _find from 'lodash/find';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _sortBy from 'lodash/sortBy';
import _isEqual from 'lodash/isEqual';
import _throttle from 'lodash/throttle';

function renderEmptyMeetingList() {
  return (
    <div className={classes.chatListMessage}>
      <FormattedMessage
        id="components.chat.chatListEmptyMeeting"
        description="Inform user that once they join a meeting chat, it will be available in the list"
        defaultMessage="Your meeting chats will{br}appear here."
        values={{
          br: <br />
        }}
      />
    </div>
  );
}

function renderEmptyContactList() {
  return (
    <div className={classes.chatListMessage}>
      <FormattedMessage
        id="components.chat.chatListEmptyContact"
        description="Inform user that once they join a contact chat, it will be available in the
        list"
        defaultMessage="Your chats with contacts{br}will appear here."
        values={{
          br: <br />
        }}
      />
    </div>
  );
}

class ChatListGroups extends Component {
  constructor(props) {
    super(props);
    this.state = {
      scrollPositions: [],
      scrollTop: false
    };
    this.renderedElement = {}; // reference to the element from ref
    this.offsetTops = []; // Array of reported offset positions of unread chats
  }

  componentDidMount() {
    window.addEventListener('resize', _throttle(this.handlePositionChange, 100));
  }

  componentWillUnmount() {
    window.removeEventListener('resize', _throttle(this.handlePositionChange, 100));
  }

  calculateOffsetHeight = (offsetTop) => {
    if (offsetTop === null) {
      this.offsetTops = [];
    } else {
      this.offsetTops.push(offsetTop);
    }
  };

  scrollToPos = (scrollType) => {
    let positionToScrollTo = false;
    if (scrollType === 'top' && this.state.scrollPositions.length) {
      const visibleScrollUps = _filter(
        this.state.scrollPositions,
        (scrollPosition) => !scrollPosition.hidden && scrollPosition.position < this.renderedElement.scrollTop
      );
      if (!visibleScrollUps.length) return;
      const sortedScrollUps = _sortBy(visibleScrollUps, 'position');
      positionToScrollTo = sortedScrollUps[sortedScrollUps.length - 1].position;
    }

    if (scrollType === 'bottom' && this.state.scrollPositions.length) {
      const visibleScrollDowns = _filter(
        this.state.scrollPositions,
        (scrollPosition) =>
          !scrollPosition.hidden &&
          scrollPosition.position >= this.renderedElement.offsetHeight + this.renderedElement.scrollTop
      );
      if (!visibleScrollDowns.length) return;
      positionToScrollTo = _sortBy(visibleScrollDowns, 'position')[0].position;
    }

    if (positionToScrollTo !== false) {
      this.setState({ scrollTop: positionToScrollTo });
    }
  };

  handlePositionChange = () => {
    if (!this.renderedElement) return;
    const scrollPositions = _map(this.state.scrollPositions, (scrollPosition) => {
      scrollPosition.hidden = // eslint-disable-line no-param-reassign
        scrollPosition.position > this.renderedElement.scrollTop &&
        scrollPosition.position <= this.renderedElement.offsetHeight + this.renderedElement.scrollTop;
      return scrollPosition;
    });

    this.setState({ scrollPositions });
  };

  render() {
    const {
      conversationContactDialString,
      isBasicUser,
      isFirstUse,
      markConversationAsRead,
      meetingConversations,
      onLeaveConversation,
      onSelectConversation,
      userConversations
    } = this.props;

    if (!isFirstUse) {
      return (
        <div className={classes.chatListWrapper}>
          <UnreadChatNav
            scrollType={'top'}
            showScroll={_find(
              this.state.scrollPositions,
              (scrollPosition) => !scrollPosition.hidden && scrollPosition.position < this.renderedElement.scrollTop
            )}
            scrollToPos={this.scrollToPos}
          />
          <div
            className={classes.chatLists}
            ref={(elt) => {
              if (!elt) return;
              this.renderedElement = elt;
              if (this.state.scrollTop) {
                // scroll to the value provided by scrollToPos
                this.renderedElement.scrollTop = this.state.scrollTop;
                this.setState({ scrollTop: false });
              }

              const scrollPositions =
                _map(this.offsetTops, (position) => {
                  const hidden = position < elt.offsetHeight + elt.scrollTop && position >= elt.scrollTop;
                  return { position, hidden };
                }) || [];

              if (!_isEqual(_map(scrollPositions, 'position'), _map(this.state.scrollPositions, 'position'))) {
                // If the new positions aren't the same as the ones in the state, save the new ones
                this.setState({ scrollPositions });
              }
            }}
            onScroll={_throttle(() => {
              this.handlePositionChange();
            }, 100)}
          >
            {(!isBasicUser || !!meetingConversations.length) && (
              <ChatContactsComp
                conversationContactDialString={conversationContactDialString}
                conversations={meetingConversations}
                emptyMessage={renderEmptyMeetingList()}
                onSelect={onSelectConversation}
                markConversationAsRead={markConversationAsRead}
                calculateOffsetHeight={this.calculateOffsetHeight}
                onRemove={(contact, index, isSelected) => {
                  onLeaveConversation(contact);
                  if (!isSelected) return;
                  if (index) {
                    onSelectConversation(meetingConversations[index - 1]);
                  } else if (meetingConversations[1]) {
                    onSelectConversation(meetingConversations[1]);
                  } else if (userConversations[0]) {
                    onSelectConversation(userConversations[0]);
                  }
                }}
                title={<FormattedMessage id="components.chat.chatListTitleMeetings" defaultMessage="Meetings" />}
              />
            )}
            <ChatContactsComp
              conversationContactDialString={conversationContactDialString}
              conversations={userConversations}
              emptyMessage={renderEmptyContactList()}
              onSelect={onSelectConversation}
              markConversationAsRead={markConversationAsRead}
              calculateOffsetHeight={this.calculateOffsetHeight}
              onRemove={(contact, index, isSelected) => {
                onLeaveConversation(contact);
                if (!isSelected) return;
                if (index) {
                  onSelectConversation(userConversations[index - 1]);
                } else if (meetingConversations.length) {
                  onSelectConversation(meetingConversations[meetingConversations.length - 1]);
                }
              }}
              title={<FormattedMessage id="components.chat.chatListTitleContacts" defaultMessage="Contacts" />}
            />
          </div>
          <UnreadChatNav
            scrollType={'bottom'}
            showScroll={_find(
              this.state.scrollPositions,
              (scrollPosition) =>
                !scrollPosition.hidden &&
                scrollPosition.position > this.renderedElement.offsetHeight + this.renderedElement.scrollTop
            )}
            scrollToPos={this.scrollToPos}
          />
        </div>
      );
    }
    return (
      <div className={`${classes.chatListMessage} ${classes.isNew}`}>
        <FormattedMessage
          id="components.chat.chatListNew"
          description="Inform user that once they start chatting, they will see a chat history in this
        location"
          defaultMessage="Once you start chatting,{br}you’ll see your chat{br}history here."
          values={{
            br: <br />
          }}
        />
      </div>
    );
  }
}

ChatListGroups.propTypes = {
  conversationContactDialString: PT.string,
  isBasicUser: PT.bool,
  isFirstUse: PT.bool,
  markConversationAsRead: PT.func,
  meetingConversations: PT.array,
  onLeaveConversation: PT.func,
  onSelectConversation: PT.func,
  userConversations: PT.array
};

ChatListGroups.defaultProps = {
  isFirstUse: false,
  meetingConversations: [],
  userConversations: []
};

export default ChatListGroups;
